import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  notification,
  Popconfirm,
  Table,
  Tooltip,
  TreeSelect,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  EuroCircleOutlined,
  FileExcelOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import AvatarStatus from "components/shared-components/AvatarStatus";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import { useNavigate, useSearchParams } from "react-router-dom";
import utils from "utils";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { isLoaded, useFirebase, useFirestore } from "react-redux-firebase";
import { deleteAssociatedFilesFromStorage } from "functions/firebase/storage";
import urls from "urls";
import { formatPrice, getFormattedProductColor } from "../../../../functions/formatters";
import { getExactProduct } from "../../../../functions/api/exact";
import Loading from "../../../../components/shared-components/Loading";
import {
  getProductCatalogueExcelExportData,
  getProductExcelExportData,
  getProductUrlsExcelExportData,
} from "functions/excel";
import _, { concat } from "lodash";
import { deleteProductLinkedVariants, updateProductPrice } from "../../../../functions/firestore/product";
import { findCategoryById, findCategoryBySlug, getProductsByCategory } from "../../../../functions/categories";
import { useReactToPrint } from "react-to-print";
import { useProductColors } from "../../../../data/hooks/useProductColors";
import { useProductMaterials } from "../../../../data/hooks/useProductMaterials";
import CataloguePDF from "../../../../components/util-components/PDF/CataloguePDF";
import { COMPANY_CONSTANTS } from "../../../../constants/CompanyConstants";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../../constants/DateConstant";
import JSZip from "jszip";
import QRCode from "qrcode";
import * as dayjs from "dayjs";
import ExcelExport from "../../../../components/shared-components/ExcelExport";

const ProductList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const firestore = useFirestore();
  const firebase = useFirebase();
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const email = useSelector((state) => state.firebaseReducer.auth.email);
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const categories = useSelector((state) => state.firestoreReducer.ordered.categories);
  const purchaseOrders = useSelector((state) => state.firestoreReducer.ordered.purchaseOrders);
  const { colors: productColors } = useProductColors();
  const { materials: productMaterials } = useProductMaterials();
  const catalogueRef = useRef(null);
  const isUpdatingUrl = useRef(true); // Start as true to prevent URL sync during init
  const [list, setList] = useState(products);
  const [filteredList, setFilteredList] = useState(products);
  const [clickedCategory, setClickedCategory] = useState(searchParams.get("category") || "all");
  const [tablePage, setTablePage] = useState(parseInt(searchParams.get("page")) || 1);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState(searchParams.get("q") || "");
  const [exportTableVisible, setExportTableVisible] = useState(false);
  const [exactFilter, setExactFilter] = useState(searchParams.get("exact") || "all");

  const handlePrintCatalogue = useReactToPrint({
    content: () => catalogueRef.current,
    onAfterPrint: () => {
      return new Promise((resolve, reject) => {
        resolve(setExportTableVisible(false));
      });
    },
    onBeforeGetContent: () => {
      return new Promise((resolve, reject) => {
        resolve(setExportTableVisible(true));
      });
    },
  });

  useEffect(() => {
    if (!!products) setIsLoading(false);
    // Update list based on category filter
    if (clickedCategory && clickedCategory !== "all") {
      const category = findCategoryBySlug(categories, clickedCategory);
      const filteredProducts = getProductsByCategory(products, category);
      setList(filteredProducts);
    } else {
      setList(products);
    }
  }, [products, clickedCategory, categories]);

  // Enable URL sync after component is mounted and initialized
  useEffect(() => {
    setTimeout(() => {
      isUpdatingUrl.current = false;
    }, 500);
  }, []);

  // Sync state to URL parameters
  useEffect(() => {
    if (isUpdatingUrl.current) return;

    const params = new URLSearchParams();
    if (clickedCategory && clickedCategory !== "all") {
      params.set("category", clickedCategory);
    }
    if (tablePage && tablePage > 1) {
      params.set("page", tablePage);
    }
    if (searchText) {
      params.set("q", searchText);
    }
    if (exactFilter && exactFilter !== "all") {
      params.set("exact", exactFilter);
    }
    setSearchParams(params);
  }, [clickedCategory, tablePage, searchText, exactFilter, setSearchParams]);

  // Apply exact filter to filtered list
  useEffect(() => {
    if (!list) return;

    let filtered = list;

    // Apply search filter
    if (searchText) {
      filtered = utils.wildCardSearchProductName(filtered, searchText);
    }

    // Apply exact filter
    if (exactFilter === "not_linked") {
      filtered = filtered.filter((product) => !product.exactGuid);
    } else if (exactFilter === "linked") {
      filtered = filtered.filter((product) => !!product.exactGuid);
    }

    setFilteredList(filtered);
  }, [list, searchText, exactFilter]);

  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item onClick={() => viewDetails(row)}>
        <Flex alignItems="center">
          <EditOutlined />
          <span style={{ marginLeft: 4 }}>{t("edit")}</span>
        </Flex>
      </Menu.Item>{" "}
      <Menu.Item onClick={() => duplicateRow(row)}>
        <Flex alignItems="center">
          <CopyOutlined />
          <span style={{ marginLeft: 4 }}>{t("duplicate")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item>
        <Popconfirm
          placement="bottom"
          title={t("confirm_delete_product", { name: row.name })}
          onConfirm={() => beforeDeleteRow(row)}
          okText={t("yes")}
          cancelText={t("no")}
        >
          <a onClick={(e) => e.preventDefault}>
            <DeleteOutlined />
            {t("delete")}
          </a>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const addProduct = () => {
    navigate(urls.addProduct);
  };

  const viewDetails = (row) => {
    navigate(urls.editProduct + row.id);
  };

  const duplicateRow = (row) => {
    const object = _.clone(row);
    delete object.id;
    object.image = null;
    object.createdAt = dayjs().unix();
    object.createdBy = email;
    object.updatedAt = dayjs().unix();
    object.updatedBy = email;
    object.name = object.name + " - Kopie";
    object.url = object.url + "-kopie";
    firestore
      .collection("products")
      .add(object)
      .then((doc) => {
        messageApi.success(t("notifications.product_duplicated", 4));
        viewDetails({ object, id: doc.id });
      })
      .catch((error) => messageApi.error(error.message, 4));
  };

  const beforeDeleteRow = (row) => {
    let associatedProducts = _.filter(products, {
      productItems: [{ productId: row.id }],
    });
    if (associatedProducts.length > 0) {
      let itemsString = "";
      _.forEach(associatedProducts, (item) => (itemsString += item.name + "\n"));
      notification.open({
        message: t("notifications.linked_products_title"),
        description: t("notifications.linked_products_description") + itemsString,
        duration: 0,
        key: row.id,
        btn: (
          <Button
            type="primary"
            onClick={() => {
              deleteRow(row);
              notification.close(row.id);
            }}
          >
            {t("proceed")}
          </Button>
        ),
      });
    } else deleteRow(row);
  };

  const deleteRow = (row) => {
    const objKey = "id";
    let data = list;
    let images = row.image;
    deleteAssociatedFilesFromStorage(firebase, images);
    firestore
      .collection("products")
      .doc(row.id)
      .delete()
      .then(() => {
        data = utils.deleteArrayRow(data, objKey, row.id);
        setList(data);
        messageApi.success(t("notifications.product_deleted", 4));
        deleteAssociatedProductItems(row.id);
      })
      .catch((error) => {
        messageApi.error(error.message, 4);
      });
    deleteProductLinkedVariants(firestore, products, row.id);
  };

  const deleteAssociatedProductItems = (id) => {
    let associatedProducts = _.filter(products, {
      productItems: [{ productId: id }],
    });
    if (associatedProducts.length > 0) {
      _.forEach(associatedProducts, (product) => {
        let productItems = product.productItems;
        let remainingItems = _.filter(productItems, (item) => {
          return item.productId !== id;
        });

        if (remainingItems.length === 0) remainingItems = null;
        firestore.collection("products").doc(product.id).update({ productItems: remainingItems });
      });
    }
  };

  const tableColumns = [
    {
      title: t("product"),
      dataIndex: "name",
      render: (_, record) => (
        <div className="d-flex">
          <AvatarStatus
            size={60}
            type="square"
            src={!!record.image ? record.image[0] : null}
            name={record.name}
            linkTo={urls.editProduct + record.id}
          />
        </div>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "name"),
    },
    {
      title: t("code"),
      dataIndex: "code",
      render: (_, product) => <span>{product.code + (product.barcode ? " / " + product.barcode : "")}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "code"),
    },
    {
      title: t("category"),
      dataIndex: "category",
      render: (category) =>
        typeof category === "object" ? (
          category.map((cat, i) => (
            <span key={i}>
              {cat}
              {i !== category.length - 1 && ","}{" "}
            </span>
          ))
        ) : (
          <span>{findCategoryById(categories, category)?.name}</span>
        ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "category"),
    },
    {
      title: t("color") + " / " + t("material"),
      dataIndex: "color",
      render: (_, record) => {
        // Use colorNew if available, fallback to color
        const colorId = record.colorNew || record.color;
        const materialValue = record.material;

        // Handle material as array or single value
        const materialIds = Array.isArray(materialValue) ? materialValue : materialValue ? [materialValue] : [];

        // Find color by ID (new) or code (legacy)
        const colorData = productColors.find((c) => c.id === colorId || c.code === colorId);
        const isLegacyColor = colorData && colorData.code === colorId && colorData.id !== colorId;

        // Find all materials by ID
        const materialsData = materialIds
          .map((materialId) => {
            const materialData = productMaterials.find((m) => m.id === materialId);
            return materialData;
          })
          .filter((m) => m);

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {colorData && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {colorData.image && (
                  <img
                    src={colorData.image}
                    alt={colorData.name}
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "4px",
                      objectFit: "cover",
                    }}
                  />
                )}
                <span style={{ color: isLegacyColor ? "#ff4d4f" : "inherit" }}>{colorData.name}</span>
              </div>
            )}
            {materialsData.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                {materialsData.map((material, index) => (
                  <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ color: "#666" }}>{material.name}</span>
                  </div>
                ))}
              </div>
            )}
            {!colorData && materialsData.length === 0 && <span style={{ color: "#999" }}>-</span>}
          </div>
        );
      },
    },
    {
      title: t("price"),
      dataIndex: "price",
      onCell: (record) => ({
        record,
        editable: true,
        dataIndex: "price",
        title: t("price"),
        handleSave,
      }),
      render: (price) => <div style={{ textWrap: "nowrap" }}>€ {formatPrice(price)}</div>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "price"),
    },
    {
      title: t("discount"),
      dataIndex: "discount",
      render: (discount) => <span>{!!discount ? discount : 0}%</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "discount"),
    },
    {
      title: t("alternatives"),
      dataIndex: "groupAlternatives",
      render: (groupAlternatives) => <span>{!!groupAlternatives ? t("yes") : t("no")}</span>,
    },
    {
      title: t("stock"),
      dataIndex: "stock",
      render: (stock, record) => {
        let preOrderTagColor = "#e6f7ff";
        let preOrderBorderColor = "#91d5ff";
        let preOrderTextColor = "#1890ff";

        if (record.preOrder?.active && record.preOrder.availableAt) {
          const isPast = dayjs(record.preOrder.availableAt, "DD-MM-YYYY").isBefore(dayjs(), "day");
          if (isPast) {
            preOrderTagColor = "#fff7e6";
            preOrderBorderColor = "#ffd591";
            preOrderTextColor = "#fa8c16";
          }
        }

        return (
          <div>
            <div>{stock}</div>
            {record.preOrder?.active && (
              <Tooltip title={`${t("pre_order_available_date")}: ${record.preOrder.availableAt || "?"}`}>
                <span
                  style={{
                    display: "inline-block",
                    padding: "1px 6px",
                    borderRadius: 3,
                    backgroundColor: preOrderTagColor,
                    border: `1px solid ${preOrderBorderColor}`,
                    color: preOrderTextColor,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    marginTop: 2,
                  }}
                >
                  🕒 {record.preOrder.availableAt || t("pre_order")}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
      sorter: (a, b) => utils.antdTableSorter(a, b, "stock"),
    },
    {
      title: t("visible_in_shop"),
      dataIndex: "hideVariant",
      render: (hideVariant) => (
        <span>
          {hideVariant ? <CloseOutlined style={{ color: "red" }} /> : <CheckOutlined style={{ color: "green" }} />}
        </span>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "stock"),
    },
    {
      title: t("synced_with_exact"),
      dataIndex: "exactGuid",
      render: (exactGuid, record) => (
        <Tooltip title={exactGuid ? exactGuid : t("click_to_link_to_exact")}>
          <span
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (exactGuid) {
                navigator.clipboard.writeText(exactGuid);
                messageApi.success(t("notifications.copied_to_clipboard", 4));
              } else {
                handleLinkToExact(record);
              }
            }}
          >
            {exactGuid ? <CheckOutlined style={{ color: "green" }} /> : <CloseOutlined style={{ color: "red" }} />}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => {
        const hasA = !!(a && a.exactGuid);
        const hasB = !!(b && b.exactGuid);
        // Put unsynced (no exactGuid) first
        if (hasA !== hasB) return hasA - hasB; // 0 (unsynced) before 1 (synced)
        if (!hasA && !hasB) return 0; // both unsynced
        // Both have values, compare strings
        return String(a.exactGuid).localeCompare(String(b.exactGuid));
      },
      filterIcon: (filtered) => <SearchOutlined style={{ color: exactFilter !== "all" ? "#1890ff" : undefined }} />,
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Button
            type={exactFilter === "all" ? "primary" : "default"}
            onClick={() => setExactFilter("all")}
            style={{ width: "100%", marginBottom: 8 }}
          >
            {t("all")}
          </Button>
          <Button
            type={exactFilter === "not_linked" ? "primary" : "default"}
            onClick={() => setExactFilter("not_linked")}
            style={{ width: "100%", marginBottom: 8 }}
          >
            {t("not_linked_to_exact")}
          </Button>
          <Button
            type={exactFilter === "linked" ? "primary" : "default"}
            onClick={() => setExactFilter("linked")}
            style={{ width: "100%" }}
          >
            {t("linked_to_exact")}
          </Button>
        </div>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const blobToBuffer = async (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener("loadend", (e) => resolve(new Uint8Array(e.target.result)));
      reader.addEventListener("error", reject);
      reader.readAsArrayBuffer(blob);
    });
  };

  const handleExportQrCodes = () => {
    if (products.length > 0) {
      const qrPromises = products.map(async (product) => {
        const base64 = await QRCode.toDataURL(process.env.REACT_APP_PRODUCT_URL + product.url, {
          width: 512,
          height: 512,
          margin: 1,
        });
        // Verwijder de 'data:image/png;base64,' prefix
        const base64Data = base64.replace(/^data:image\/png;base64,/, "");
        // Converteer base64 naar binaire data
        const binaryData = atob(base64Data);
        // Converteer binaire string naar Uint8Array
        const uint8Array = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          uint8Array[i] = binaryData.charCodeAt(i);
        }
        return { filename: `${product.code}.png`, fileContent: uint8Array };
      });

      Promise.all(qrPromises)
        .then((files) => {
          const zip = new JSZip();
          files.forEach((file) => {
            zip.file(file.filename, file.fileContent);
          });
          return zip.generateAsync({ type: "blob" });
        })
        .then((content) => {
          const url = window.URL.createObjectURL(content);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Product QR-codes.zip`);
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 0);
        })
        .catch((error) => {
          console.error("Error in handleExportQrCodes:", error);
          // Hier kun je een foutmelding aan de gebruiker tonen
        });
    }
  };

  const exportMenu = (
    <Menu>
      <Menu.Item key="1">
        <ExcelExport
          data={getProductExcelExportData(products, categories)}
          fileName={COMPANY_CONSTANTS.WEBSITE_NAME + " producten " + dayjs().format(DATE_FORMAT_DD_MM_YYYY)}
        >
          {t("excel_products")}
        </ExcelExport>
      </Menu.Item>
      <Menu.Item key="2">
        <ExcelExport
          data={getProductUrlsExcelExportData(products)}
          fileName={COMPANY_CONSTANTS.WEBSITE_NAME + " product urls " + dayjs().format(DATE_FORMAT_DD_MM_YYYY)}
        >
          {t("excel_product_urls")}
        </ExcelExport>
      </Menu.Item>
      <Menu.Item key="3">
        <ExcelExport
          data={getProductCatalogueExcelExportData(products, categories)}
          fileName={COMPANY_CONSTANTS.WEBSITE_NAME + " product catalogus " + dayjs().format(DATE_FORMAT_DD_MM_YYYY)}
        >
          {t("excel_product_catalogue")}
        </ExcelExport>
      </Menu.Item>
      <Menu.Item key="4">
        <a href={"#"} onClick={handleExportQrCodes}>
          {t("product_images_qr_codes")}
        </a>
      </Menu.Item>
    </Menu>
  );

  const onSearch = (e) => {
    const value = e.currentTarget.value;
    setSearchText(value);
  };

  const handleShowCategory = (slug) => {
    setClickedCategory(slug);
    // Only reset search and pagination if explicitly changing category
    // Don't reset if coming back from navigation with preserved state
    if (slug !== clickedCategory) {
      setSearchText("");
      setTablePage(1);
    }
    if (slug !== "all") {
      const category = findCategoryBySlug(categories, slug);
      const filteredProducts = getProductsByCategory(products, category);
      setList(filteredProducts);
      setFilteredList(filteredProducts);
    } else {
      setList(products);
      setFilteredList(products);
    }
  };

  const onChangeTablePage = (value) => {
    setTablePage(value);
  };

  const handleSave = (row, initialPrice) => {
    const newPrice = row.price;
    if (newPrice !== initialPrice && newPrice > 0) {
      const id = row.id;
      updateProductPrice(id, newPrice);
    }
  };

  const handleLinkToExact = async (product) => {
    if (!product.code) {
      messageApi.warning(t("product_code_required_for_exact_link"));
      return;
    }

    const hideLoading = messageApi.loading(t("searching_in_exact"), 0);

    try {
      const exactProduct = await getExactProduct(product.code);

      if (!exactProduct) {
        hideLoading();
        messageApi.error(t("product_not_found_in_exact", { code: product.code }));
        return;
      }

      // Update product with exactGuid and barcode from Exact
      const updateObject = {
        exactGuid: exactProduct.Id,
        barcode: exactProduct.Barcode || null,
      };

      firestore
        .collection("products")
        .doc(product.id)
        .update(updateObject)
        .then(() => {
          hideLoading();
          messageApi.success(t("product_linked_to_exact"));
        })
        .catch((error) => {
          hideLoading();
          messageApi.error(error.message);
        });
    } catch (error) {
      hideLoading();
      messageApi.error(error.message || t("error_linking_to_exact"));
    }
  };

  const getCatalogueData = () => {
    const clonedProducts = _.cloneDeep(products);
    // Remove all products where code begins with HG or MG
    const filteredProducts = _.filter(clonedProducts, (product) => {
      return !product.code.startsWith("HG") && !product.code.startsWith("MG");
    });

    // Create an object to store the categories and their products
    let categories = {};

    // Create a set to store the ids of the products that have been added
    let addedProductIds = new Set();

    // Go through each product
    filteredProducts.forEach((product) => {
      // For each product, check if its category exists in the categories object
      const category = product.category[product.category.length - 1];
      if (!categories[category]) {
        // If the category does not exist, create a new array for it
        categories[category] = [];
      }

      // If the product has variants and it has not been added yet, add it and its variants
      if (product.productVariants && !addedProductIds.has(product.id)) {
        categories[category].push(product);
        addedProductIds.add(product.id);
        product.productVariants.forEach((variant) => {
          const variantProduct = filteredProducts.find((p) => p.id === variant.productId);
          if (variantProduct && !addedProductIds.has(variantProduct.id)) {
            categories[category].push(variantProduct);
            addedProductIds.add(variantProduct.id);
          }
        });
      } else if (!product.productVariants && !addedProductIds.has(product.id)) {
        // If the product does not have variants and it has not been added yet, add it
        categories[category].push(product);
        addedProductIds.add(product.id);
      }
    });

    return Object.values(categories).flat();
  };

  const components = {
    body: {
      row: PriceRow,
      cell: PriceCell,
    },
  };

  return (
    <>
      {contextHolder}
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              value={searchText}
              placeholder={t("search")}
              prefix={<SearchOutlined />}
              onChange={(e) => onSearch(e)}
            />
            <TreeSelect
              showSearch
              onChange={handleShowCategory}
              style={{ width: "100%", minWidth: 150 }}
              placeholder={t("form.choose_category")}
              allowClear
              treeDefaultExpandAll
              value={clickedCategory || "all"}
              treeData={concat([{ slug: "all" }], categories).map((category) => {
                return {
                  key: category?.slug,
                  title: category?.slug === "all" ? t("all_categories") : category?.name,
                  value: category?.slug,
                  children:
                    !!category?.subcategories &&
                    category.subcategories.map((subcategory) => {
                      return {
                        title: subcategory.name,
                        value: subcategory.slug,
                        key: subcategory.slug,
                      };
                    }),
                };
              })}
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={addProduct} type="primary" icon={<PlusCircleOutlined />} block className="w-auto">
              {t("add_product")}
            </Button>
            <Dropdown overlay={exportMenu}>
              <Button className="ant-btn-primary">
                <FileExcelOutlined /> {t("export")} <DownOutlined />
              </Button>
            </Dropdown>
            <Button onClick={() => navigate(urls.productPriceImport)} icon={<EuroCircleOutlined />}>
              {t("price_import.open_button")}
            </Button>
            <Button onClick={handlePrintCatalogue} icon={<DownloadOutlined />}>
              {t("export_catalogue")}
            </Button>
          </div>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <div className="table-responsive">
            <Table
              components={components}
              columns={tableColumns}
              dataSource={filteredList}
              rowKey="id"
              pagination={{
                current: !!tablePage ? tablePage : 1,
                onChange: onChangeTablePage,
              }}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_products")} />,
              }}
              scroll={{ x: 1300 }}
              loading={!isLoaded(products)}
            />
          </div>
        )}
      </Card>
      <div style={{ display: "none" }}>
        {exportTableVisible && (
          <>
            <CataloguePDF ref={catalogueRef} data={getCatalogueData()} />
          </>
        )}
      </div>
    </>
  );
};

const EditableContext = React.createContext(null);

const PriceRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const PriceCell = ({ title, editable, children, dataIndex, record, handleSave, ...restProps }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [initialPrice, setInitialPrice] = useState(null);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
      if (!initialPrice) setInitialPrice(record.price);
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values }, initialPrice);
    } catch (err) {
      console.log("Save failed:", err);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: t("form.enter_price"),
          },
        ]}
      >
        <InputNumber
          ref={inputRef}
          onPressEnter={save}
          onBlur={save}
          className="w-100"
          formatter={(value) => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        />
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" onClick={toggleEdit}>
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

export default ProductList;
